import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Block } from 'components';
import { actions, duck } from 'coupon-common/src/modules/businessCategories';
import {
  atLeastOneSelected,
  getNormalizedEntity,
} from 'coupon-common/src/utils';

import '../Authentication/AuthFlow.css';

import messages from './messages';

/* ── inline search icon, matches ServiceableAreaSection's ── */
function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="6.3" cy="6.3" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ── search + pill selector, styled to match the Serviceable Area
   city picker (auth-serviceable-box / auth-city-pill-row / auth-city-pill).
   Bound to a react-final-form Field's { input, meta } instead of the
   city selector's local state, and supports single- or multi-select
   via maxSelected. ── */
function CategoryPillField({
  input,
  meta,
  options = [],
  isLoading,
  loadingError,
  maxSelected,
  readOnly,
  searchPlaceholder,
  showSearch = true,
}) {
  const [query, setQuery] = useState('');
  const selected = Array.isArray(input.value) ? input.value : [];
  const selectedIds = new Set(selected.map(o => o.id));

  const filtered = showSearch && query.trim()
    ? options.filter(o => o.name.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  const toggleOption = option => {
    if (readOnly) return;

    if (selectedIds.has(option.id)) {
      input.onChange(selected.filter(o => o.id !== option.id));
      return;
    }

    if (maxSelected === 1) {
      // single-select: picking a new option replaces the old one
      input.onChange([option]);
      return;
    }

    if (selected.length < maxSelected) {
      input.onChange([...selected, option]);
    }
  };

  return (
    <div className="auth-serviceable-box">
      {showSearch && (
        <label className="auth-field auth-field-plain auth-serviceable-search">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
          />
          <SearchIcon />
        </label>
      )}

      {isLoading && <p className="text-muted">Loading…</p>}
      {loadingError && (
        <span className="auth-field-error">Something went wrong loading options.</span>
      )}

      {!isLoading && !loadingError && (
        <div className="auth-city-pill-row">
          {filtered.map(o => (
            <button
              key={o.id}
              type="button"
              className={`auth-city-pill${selectedIds.has(o.id) ? ' is-selected' : ''}`}
              onClick={() => toggleOption(o)}
              disabled={readOnly}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {meta.touched && meta.error && (
        <span className="auth-field-error">{meta.error}</span>
      )}
    </div>
  );
}

const BusinessCategoriesField = ({
  intl,
  businessCategories,
  readOnly,
  fullWidth,
  dispatch,
}) => {
  useEffect(() => {
    dispatch(actions.getBusinessCategories());
  }, [dispatch]);

  const businessCategoriesKey = duck.businessCategoriesEntity.key;
  const form = useForm();
  const {
    entityList: businessCategoriesOptions,
    isFetching: businessCategoriesFetching,
    error: businessCategoriesLoadingError,
    /**
     * Pagination waiting for larger categories list:
     * isNextPageExist: businessCategoriesNextPageExist,
     */
  } = getNormalizedEntity(
    businessCategories,
    businessCategoriesKey,
    intl,
    messages.entityBusinessCategories,
  );

  const { values: { categories = [], subcategories } = {} } = useFormState({
    subscription: { values: true },
  });

  const selectedCategoryId = categories[0]?.id;

  let selectedCategory;

  const subcategoryOptions = useMemo(() => {
    selectedCategory = businessCategoriesOptions.find(c => c.id === selectedCategoryId);
    return selectedCategory?.subcategories || [];
  }, [selectedCategoryId, businessCategoriesOptions]);

  // When the user changes the category, empty the subcategories field so that it doesn't store the subcategories of previous category with current category
  useEffect(()=>{
    if(categories?.length > 0){
      let newSubcategories = subcategories?.filter((subCat)=> subCat?.category === categories[0]?.id)
      newSubcategories = newSubcategories?.length > 0 ? newSubcategories : undefined
      form.change('subcategories', newSubcategories);
    }
  }, [categories])

  return (
    <Block>
      <div className="row justify-content-center">
        <div className={fullWidth ? 'col-md-12' : 'col-md-10'}>
          <h5>
            <FormattedMessage {...messages.blockHeading} />
          </h5>
          <p className="text-muted">
            <FormattedMessage {...messages.instruction} />
          </p>
          <Field
            name="categories"
            validate={atLeastOneSelected(messages.atLeastOneMessage)}
          >
            {({ input, meta }) => (
              <CategoryPillField
                input={input}
                meta={meta}
                options={businessCategoriesOptions}
                isLoading={businessCategoriesFetching}
                loadingError={businessCategoriesLoadingError}
                maxSelected={1}
                readOnly={readOnly}
                showSearch={false}
              />
            )}
          </Field>

          {selectedCategory && subcategoryOptions?.length > 0 && (
            <>
              <hr />
              <h6>
                <FormattedMessage {...messages.subcatBlockHeading} />
              </h6>
              <p className="text-muted">
                <FormattedMessage {...messages.subcatInstruction} />
              </p>
              <Field
                name="subcategories"
                defaultValue={[]}
                validate={atLeastOneSelected(messages.atLeastOneMessage)}
              >
                {({ input, meta }) => (
                  <CategoryPillField
                    input={input}
                    meta={meta}
                    options={subcategoryOptions}
                    maxSelected={5}
                    readOnly={readOnly}
                    searchPlaceholder="Search subcategories…"
                  />
                )}
              </Field>
            </>
          )}
        </div>
      </div>
    </Block>
  );
};

BusinessCategoriesField.propTypes = {
  intl: PropTypes.object.isRequired,
  businessCategories: PropTypes.object,
  readOnly: PropTypes.bool,
  fullWidth: PropTypes.bool,
  dispatch: PropTypes.func,
};

const mapStateToProps = state => ({
  businessCategories: state.businessCategories,
});

export default connect(mapStateToProps)(injectIntl(BusinessCategoriesField));